import React, { useState, useMemo, useCallback } from 'react';
import { useGetTemplates } from '../functions/templatesHook';
import SearchInput from '../components/Template/SearchInput';
import Filters from '../components/Template/FiltersSection';
import TemplateGrid from '../components/Template/TemplateGrid';
import FloatingAdTwo from '../components/HomeAdTwo.tsx'

type Filters = {
  platform: string;
  category: string;
  priceRange: string;
  priceFilterType: string; 
  priceMin: string; 
  priceMax: string; 
};

const TemplateGridSkeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="aspect-video bg-gray-200 w-full"></div>
          <div className="p-6 space-y-4">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
            <div className="flex justify-between items-center">
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            </div>
            <div className="h-12 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Templates: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    platform: '',
    category: '',
    priceRange: '',
    priceFilterType: '',
    priceMin: '',
    priceMax: '',
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const templatesPerPage = 6;

  const { templates, totalPages, isLoading } = useGetTemplates(currentPage, templatesPerPage);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); 
  }, []);

  const handleFilterChange = useCallback((key: keyof Filters, value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
    setCurrentPage(1); 
  }, []);

  const handlePriceMinChange = useCallback((value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      priceMin: value,
    }));
  }, []);

  const handlePriceMaxChange = useCallback((value: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      priceMax: value,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      platform: '',
      category: '',
      priceRange: '',
      priceFilterType: '',
      priceMin: '',
      priceMax: '',
    });
    setCurrentPage(1);
  }, []);

  const filteredTemplates = useMemo(() => {
    return templates?.filter((template) => {
      const matchesCategory = filters.category
        ? template.category?.toLowerCase().includes(filters.category.toLowerCase())
        : true;
      const matchesPlatform = filters.platform
        ? template.marketplace.toLowerCase().includes(filters.platform.toLowerCase())
        : true;

      const matchesPrice = () => {
        if (filters.priceFilterType === 'lessThan') {
          return template.price < parseInt(filters.priceRange);
        }
        if (filters.priceFilterType === 'greaterThan') {
          return template.price > parseInt(filters.priceRange);
        }
        if (filters.priceFilterType === 'between') {
          const min = parseInt(filters.priceMin);
          const max = parseInt(filters.priceMax);
          return template.price >= min && template.price <= max;
        }
        return true; 
      };

      const matchesSearch = searchTerm
        ? template.title.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      return matchesCategory && matchesPlatform && matchesPrice() && matchesSearch;
    }) || [];
  }, [templates, filters, searchTerm]);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <FloatingAdTwo/>
      <div className="max-w-7xl mx-auto">
        <SearchInput placeholder="Search templates..." onSearch={handleSearchChange} />

        <Filters
          filters={filters}
          categories={[
            'Portfolio', 'E-commerce', 'Corporate and Business', 'Blogs', 'Education',
            'Health and Wellness', 'Restaurants and Bars', 'Creative Services', 'Real Estate',
            'Technology and Software', 'Events and Conferences', 'Travel and Tourism',
            'Music and Entertainment', 'Customizable', 'News and Magazines', 'Non-Profit',
            'Fitness and Sports', 'Web Design Portfolios', 'Business and Finance Blogs', 'Landing Pages'
          ]}
          priceRanges={[
            { value: '50', label: ' $50' },
            { value: '100', label: ' $100' },
            { value: '200', label: ' $200' },
          ]}
          priceFilterTypes={[
            { value: 'lessThan', label: 'Less than' },
            { value: 'greaterThan', label: 'Greater than' },
            { value: 'between', label: 'Between' },
          ]}
          onFilterChange={handleFilterChange}
          onPriceMinChange={handlePriceMinChange}
          onPriceMaxChange={handlePriceMaxChange}
          onClearFilters={handleClearFilters}
        />

        {isLoading ? (
          <div className="mt-12">
            <TemplateGridSkeleton />
          </div>
        ) : (
          <TemplateGrid
            templates={filteredTemplates}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default Templates;